import React, { useCallback, useEffect, useState } from 'react';
import {
    AlertCircle,
    DollarSign,
    RefreshCw,
    ShoppingCart,
    TrendingUp,
    Users,
} from 'lucide-react';
import { GlassButton, GlassCard, KPICard } from '../ui';
import {
    getRevenueSummary,
    getRevenueByProduct,
    getRevenueTimeline,
    getTopBuyers,
} from '../../services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RevenueSummary {
    total_revenue: number;
    mtd_revenue: number;
    total_orders: number;
    mtd_orders: number;
    avg_order_value: number;
    active_subscribers: number;
    unique_customers: number;
}

interface ProductRevenue {
    product_id: string;
    name: string;
    category: string;
    order_count: number;
    revenue: number;
}

interface TimelinePoint {
    date: string;
    revenue: number;
    orders: number;
}

interface TopBuyer {
    email: string;
    name?: string;
    order_count: number;
    total_spent: number;
    last_purchase_at: string;
}

type Period = 'week' | 'month' | 'year';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = (amount: number): string =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

function formatDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return iso;
    }
}

// ---------------------------------------------------------------------------
// Period Selector
// ---------------------------------------------------------------------------

const PERIOD_OPTIONS: { value: Period; label: string }[] = [
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'year', label: 'Year' },
];

const PeriodSelector: React.FC<{ value: Period; onChange: (p: Period) => void }> = ({ value, onChange }) => (
    <div style={{ display: 'flex', gap: 'var(--space-1)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', padding: '3px', border: '1px solid var(--stroke)' }}>
        {PERIOD_OPTIONS.map(opt => (
            <button
                key={opt.value}
                onClick={() => onChange(opt.value)}
                style={{
                    padding: '5px 14px',
                    borderRadius: 'var(--radius-sm)',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 'var(--text-sm)',
                    fontWeight: value === opt.value ? 'var(--font-semibold)' : 'var(--font-normal)',
                    background: value === opt.value ? 'var(--primary-500)' : 'transparent',
                    color: value === opt.value ? '#ffffff' : 'var(--text-secondary)',
                    transition: 'all var(--transition-normal)',
                }}
            >
                {opt.label}
            </button>
        ))}
    </div>
);

// ---------------------------------------------------------------------------
// Timeline Chart (CSS bar chart)
// ---------------------------------------------------------------------------

const TimelineChart: React.FC<{ data: TimelinePoint[] }> = ({ data }) => {
    if (!data || data.length === 0) {
        return (
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', margin: 0 }}>
                No timeline data for this period.
            </p>
        );
    }

    const maxRevenue = Math.max(...data.map(d => d.revenue), 1);

    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
                <thead>
                    <tr>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 'var(--font-medium)', borderBottom: '1px solid var(--divider)' }}>Date</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 'var(--font-medium)', borderBottom: '1px solid var(--divider)', whiteSpace: 'nowrap' }}>Revenue</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 'var(--font-medium)', borderBottom: '1px solid var(--divider)' }}>Orders</th>
                        <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--divider)', minWidth: 120 }}></th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((point, i) => (
                        <tr key={i}>
                            <td style={{ padding: '8px 8px', color: 'var(--text)', borderBottom: '1px solid var(--divider)' }}>{point.date}</td>
                            <td style={{ padding: '8px 8px', color: 'var(--text)', fontWeight: 'var(--font-medium)', textAlign: 'right', borderBottom: '1px solid var(--divider)', whiteSpace: 'nowrap' }}>
                                {fmt(point.revenue)}
                            </td>
                            <td style={{ padding: '8px 8px', color: 'var(--text-secondary)', textAlign: 'right', borderBottom: '1px solid var(--divider)' }}>
                                {point.orders}
                            </td>
                            <td style={{ padding: '8px 8px', borderBottom: '1px solid var(--divider)' }}>
                                <div style={{ height: 8, background: 'var(--panel-strong)', borderRadius: 'var(--radius-full)', overflow: 'hidden', border: '1px solid var(--stroke)' }}>
                                    <div
                                        style={{
                                            height: '100%',
                                            width: `${Math.round((point.revenue / maxRevenue) * 100)}%`,
                                            background: 'var(--primary-500)',
                                            borderRadius: 'var(--radius-full)',
                                            transition: 'width 0.4s ease',
                                        }}
                                    />
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ---------------------------------------------------------------------------
// RevenueAnalytics Tab
// ---------------------------------------------------------------------------

const RevenueAnalytics: React.FC = () => {
    const [summary, setSummary] = useState<RevenueSummary | null>(null);
    const [products, setProducts] = useState<ProductRevenue[]>([]);
    const [timeline, setTimeline] = useState<TimelinePoint[]>([]);
    const [buyers, setBuyers] = useState<TopBuyer[]>([]);
    const [period, setPeriod] = useState<Period>('month');
    const [loading, setLoading] = useState(true);
    const [timelineLoading, setTimelineLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [summaryData, productsData, buyersData] = await Promise.all([
                getRevenueSummary(),
                getRevenueByProduct(),
                getTopBuyers(),
            ]);
            setSummary(summaryData);
            setProducts(Array.isArray(productsData) ? productsData : []);
            setBuyers(Array.isArray(buyersData) ? buyersData : []);
        } catch {
            setError('Failed to load revenue data.');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadTimeline = useCallback(async (p: Period) => {
        setTimelineLoading(true);
        try {
            const data = await getRevenueTimeline(p);
            setTimeline(Array.isArray(data) ? data : []);
        } catch {
            setTimeline([]);
        } finally {
            setTimelineLoading(false);
        }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => { loadTimeline(period); }, [loadTimeline, period]);

    if (loading) {
        return (
            <div className="analytics-loading">
                <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                Loading revenue data…
            </div>
        );
    }

    if (error) {
        return (
            <div className="analytics-empty">
                <AlertCircle size={18} /> {error}
            </div>
        );
    }

    // Empty state: no orders at all
    const hasData = summary && (summary.total_orders > 0 || summary.total_revenue > 0);
    if (!hasData) {
        return (
            <GlassCard variant="default" padding="lg">
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-4)', textAlign: 'center', padding: 'var(--space-8) 0' }}>
                    <DollarSign size={40} style={{ color: 'var(--text-muted)' }} />
                    <div>
                        <h3 style={{ margin: '0 0 var(--space-2) 0', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
                            No sales yet
                        </h3>
                        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', maxWidth: 420 }}>
                            Publish a product and start promoting to see revenue here.
                        </p>
                    </div>
                </div>
            </GlassCard>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            {/* KPI Row */}
            <div className="analytics-kpi-row">
                <KPICard
                    value={fmt(summary?.total_revenue ?? 0)}
                    label="Total Revenue"
                    icon={<DollarSign size={20} />}
                />
                <KPICard
                    value={fmt(summary?.mtd_revenue ?? 0)}
                    label="Month-to-Date Revenue"
                    icon={<TrendingUp size={20} />}
                />
                <KPICard
                    value={summary?.total_orders ?? 0}
                    label="Total Orders"
                    icon={<ShoppingCart size={20} />}
                    delta={`+${summary?.mtd_orders ?? 0} MTD`}
                />
                <KPICard
                    value={fmt(summary?.avg_order_value ?? 0)}
                    label="Avg Order Value"
                    icon={<Users size={20} />}
                    delta={`${summary?.unique_customers ?? 0} customers`}
                />
            </div>

            {/* Timeline */}
            <GlassCard variant="default" padding="md">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                    <h3 className="analytics-section-title" style={{ margin: 0 }}>Revenue Timeline</h3>
                    <PeriodSelector value={period} onChange={setPeriod} />
                </div>
                {timelineLoading ? (
                    <div className="analytics-loading" style={{ justifyContent: 'flex-start' }}>
                        <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        Loading…
                    </div>
                ) : (
                    <TimelineChart data={timeline} />
                )}
            </GlassCard>

            {/* Products + Buyers */}
            <div className="analytics-grid-2">
                {/* Top Products */}
                <GlassCard variant="default" padding="md">
                    <h3 className="analytics-section-title">Top Products</h3>
                    {products.length === 0 ? (
                        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', margin: 0 }}>No product sales data yet.</p>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', padding: '6px 0', color: 'var(--text-secondary)', fontWeight: 'var(--font-medium)', borderBottom: '1px solid var(--divider)' }}>Product</th>
                                    <th style={{ textAlign: 'right', padding: '6px 0', color: 'var(--text-secondary)', fontWeight: 'var(--font-medium)', borderBottom: '1px solid var(--divider)' }}>Orders</th>
                                    <th style={{ textAlign: 'right', padding: '6px 0', color: 'var(--text-secondary)', fontWeight: 'var(--font-medium)', borderBottom: '1px solid var(--divider)' }}>Revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map(p => (
                                    <tr key={p.product_id}>
                                        <td style={{ padding: '8px 0', color: 'var(--text)', borderBottom: '1px solid var(--divider)' }}>
                                            <div style={{ fontWeight: 'var(--font-medium)' }}>{p.name}</div>
                                            {p.category && (
                                                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 2 }}>{p.category}</div>
                                            )}
                                        </td>
                                        <td style={{ padding: '8px 0', color: 'var(--text-secondary)', textAlign: 'right', borderBottom: '1px solid var(--divider)' }}>{p.order_count}</td>
                                        <td style={{ padding: '8px 0', color: 'var(--text)', fontWeight: 'var(--font-medium)', textAlign: 'right', borderBottom: '1px solid var(--divider)', whiteSpace: 'nowrap' }}>
                                            {fmt(p.revenue)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </GlassCard>

                {/* Top Buyers */}
                <GlassCard variant="default" padding="md">
                    <h3 className="analytics-section-title">Top Buyers</h3>
                    {buyers.length === 0 ? (
                        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', margin: 0 }}>No buyer data yet.</p>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', padding: '6px 0', color: 'var(--text-secondary)', fontWeight: 'var(--font-medium)', borderBottom: '1px solid var(--divider)' }}>Buyer</th>
                                    <th style={{ textAlign: 'right', padding: '6px 0', color: 'var(--text-secondary)', fontWeight: 'var(--font-medium)', borderBottom: '1px solid var(--divider)' }}>Orders</th>
                                    <th style={{ textAlign: 'right', padding: '6px 0', color: 'var(--text-secondary)', fontWeight: 'var(--font-medium)', borderBottom: '1px solid var(--divider)' }}>Spent</th>
                                </tr>
                            </thead>
                            <tbody>
                                {buyers.map((b, i) => (
                                    <tr key={i}>
                                        <td style={{ padding: '8px 0', color: 'var(--text)', borderBottom: '1px solid var(--divider)' }}>
                                            {b.name && <div style={{ fontWeight: 'var(--font-medium)' }}>{b.name}</div>}
                                            <div style={{ fontSize: b.name ? 'var(--text-xs)' : 'var(--text-sm)', color: b.name ? 'var(--text-muted)' : 'var(--text)' }}>{b.email}</div>
                                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 2 }}>Last: {formatDate(b.last_purchase_at)}</div>
                                        </td>
                                        <td style={{ padding: '8px 0', color: 'var(--text-secondary)', textAlign: 'right', borderBottom: '1px solid var(--divider)' }}>{b.order_count}</td>
                                        <td style={{ padding: '8px 0', color: 'var(--text)', fontWeight: 'var(--font-medium)', textAlign: 'right', borderBottom: '1px solid var(--divider)', whiteSpace: 'nowrap' }}>
                                            {fmt(b.total_spent)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </GlassCard>
            </div>
        </div>
    );
};

export default RevenueAnalytics;
